import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import {
  FcInsurancePlanDataDto,
  FcLaboratoryBillCodeItemDto,
  FcModifyLaboratoryBillCode,
  FormGroupValidationRules,
  HL7Partner
} from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-laboratory-bill-code-list-modal',
  templateUrl: './fc-laboratory-bill-code-list-modal.component.html'
})
export class FcLaboratoryBillCodeListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-laboratory-bill-code-item';

  public idControl: FormControl = new FormControl<string>('');
  public externalIdControl: FormControl = new FormControl<string>('');
  public nameControl: FormControl = new FormControl<string>('');
  public notesControl: FormControl = new FormControl<string>('');
  public inactiveControl: FormControl = new FormControl<boolean>(false);
  public userDefinedControl: FormControl = new FormControl<boolean>(true);
  public insurancePayerPlanUidControl: FormControl = new FormControl<string>('');
  public hl7PartnerUidControl: FormControl = new FormControl<string>('');

  public selectedInsurancePlanPayerControlPks: string[] = [];
  public selectedHl7PartnerControlPks: string[] = [];

  public form: FormGroup = new FormGroup({
    id: this.idControl,
    externalId: this.externalIdControl,
    name: this.nameControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
    insurancePlanPayer: this.insurancePayerPlanUidControl,
    hl7Partner: this.hl7PartnerUidControl
  });

  public listItem: FcLaboratoryBillCodeItemDto = {
    Id: '',
    ExternalID: '',
    Name: '',
    Notes: '',
    Inactive: false,
    UserDefined: true,
    InsurancePlanUid: '',
    HL7PartnerUid: ''
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    ExternalID: {
      MaxLength: 25,
    },
    Name: {
      IsRequired: true,
      MaxLength: 100,
    },
    ANSICode: {
      MaxLength: 10,
    },
    Notes: {
      MaxLength: 65535,
    },
    InsurancePlanPayer: {
      IsRequired: true
    },
    HL7Partner: {
      IsRequired: true
    }
  };

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem(this.listItemDefaultValidation);
  }

  public setForm(): void {
    this.idControl.setValue(this.listItem?.Id);
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem?.ExternalID);

    // Name Control
    const isNameControlDisabled = !this.listItem.UserDefined;
    if (isNameControlDisabled) {
      this.nameControl.disable();
    }
    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem?.Name);

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem?.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    // User Defined Control
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined);

    if (this.listItem.InsurancePlanUid) {
      this.selectedInsurancePlanPayerControlPks.push(this.listItem?.InsurancePlanUid);
    }
    if (this.listItem.HL7PartnerUid) {
      this.selectedHl7PartnerControlPks.push(this.listItem?.HL7PartnerUid);
    }

    this.validateForm();
  }

  public onInsurancePlanDataPopulate(insurancePlanArray: FcInsurancePlanDataDto[]): void {
    const insurancePlanData = { ...insurancePlanArray[0] };
    if (insurancePlanData && insurancePlanArray[0]) {
      this.insurancePayerPlanUidControl.setValue(insurancePlanData.InsurancePlanUid);
      this.selectedInsurancePlanPayerControlPks.push(insurancePlanData.InsurancePlanUid);
    } else {
      this.insurancePayerPlanUidControl.setValue('');
      this.selectedInsurancePlanPayerControlPks = [];
    }
  }

  public onHl7PartnerDataPopulate(hl7PartnerArray: HL7Partner[]): void {
    const hl7PartnerData = { ...hl7PartnerArray[0] };
    if (hl7PartnerData && hl7PartnerArray[0]) {
      this.hl7PartnerUidControl.setValue(hl7PartnerData.HL7PartnerUid);
      this.selectedHl7PartnerControlPks.push(hl7PartnerData.HL7PartnerUid);
    } else {
      this.hl7PartnerUidControl.setValue('');
      this.selectedHl7PartnerControlPks = [];
    }
  }

  public getFindControlValidation(controlName: string): boolean {
    if (this.fcListValidation.validations[controlName]) {
      return !!this.fcListValidation.validations[controlName]?.IsRequired;
    } else {
      return !!this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  public override save(): void {
    this.isSubmitted = true;
    const values = this.form.getRawValue();
    const modifyItemDto: FcModifyLaboratoryBillCode = {
      ...(values.id && {Id: values.id}),
      HL7PartnerUid: values.hl7Partner,
      InsurancePlanUid: values.insurancePlanPayer,
      Name: values.name,
      ExternalID: values.externalId,
      Inactive: values.inactive,
      Notes: values.notes,
    };

    super.save<FcModifyLaboratoryBillCode>(modifyItemDto);
  }
}
